/**
 * @file InvoiceDetailReport.js
 * @version 1.11.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-05-21
 * @modified 2026-11-03
 * @description Xero-style Invoice Detail report. Filter bar at the top,
 *              grouped + subtotalled table below. (Unit-price variations are
 *              Variation Review's, opened from Pricing & Costing.)
 *
 *              Columns (12 default): Date, Source, Ref, Item Code, Desc,
 *              Qty, Unit Price, Discount, VAT, Gross, Total, Status.
 *              Grouped by contact family (parent + children kept together,
 *              parent first). Subtotals per group and/or at the bottom.
 *
 *              1.11.0 (3 Nov 2026, Cam — "take this out of the Invoice
 *              Detail report component (removing the button for variation
 *              report from that component's header)"): the Variation Summary
 *              button and window are gone — Variation Review is opened from
 *              Pricing & Costing's header (VariationReview).
 *
 *              1.10.0 (3 Nov 2026, Cam — month end): Variation Summary is
 *              greyed, with the reason as its title ("Choose a From and To
 *              date before today — 03/11/2026"), unless the report's From and
 *              To are both before today (London); the server refuses the same
 *              (400 VARIATION_PERIOD). The window is now a review: approve or
 *              not each variation, then act on the ones not approved
 *              (VariationSummaryModal 2.0.0).
 *
 *              1.9.0 (2 Nov 2026, Cam): the totals and the export cover
 *              every line on the filters, not the page on screen. The server
 *              groups and sorts (so a group cut by the page carries on, headed
 *              "(continued)", its subtotal once — the WHOLE group's — where
 *              its last line is), the bottom row is "Total — all N lines",
 *              and the PDF is built on the server from every line (or the
 *              page, if chosen; at most 10,000 lines). Columns, headings and
 *              figures: utils/invoiceDetailReport.js, shared with the server.
 *              Clicking a header sorts on the server, across every page.
 *
 *              1.8.3 (15 Oct 2026): Unit is ex VAT and Gross is net + VAT on
 *              every invoice, till ones included — the server now works them
 *              out (utils/xeroReports.invoiceLineMoneySql); Gross used to be
 *              the stored line total, net on an ordinary invoice.
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { API_URL } from '../config';
import './SearchLayout.css';
import './InvoiceDetailReport.css';
import Dropdown from './Dropdown';
import DateInput from './DateInput';
import { resolveDateRange as sharedResolveDateRange } from './DateRangeFilter';
import LoadingSpinner from './LoadingSpinner';
import MultiSelectDropdown from './MultiSelectDropdown';
import PaginationControls from './PaginationControls';
import ContactModal from './ContactModal';
import InvoiceModal from './InvoiceModal';
import TableExportModal from './TableExportModal';
import ColumnFilterMenu from './ColumnFilterMenu';
import { showAlert } from './Alert';
import { formatCurrency, formatPrice } from '../utils/currencyFormatter';
import { formatQuantity } from '../utils/numberFormatters';
import { formatDateShort } from '../utils/dateFormatters';
import { useSlideOutButton } from './SlideOutButton';
import usePersistedState from '../hooks/usePersistedState';
import { activeHeaderFilters, clearFiltersClass } from '../utils/activeFilters';
import { ACCOUNT_NAMES } from '../utils/accountCodes';
import {
  INVOICE_DETAIL_COLUMNS as COLUMNS,
  INVOICE_DETAIL_GROUPINGS as GROUPING_OPTIONS,
  INVOICE_DETAIL_TOTALS_MODES as TOTALS_MODES,
  formatDateCompact,
  groupRows,
  grandTotalLabel,
  isTotalColumn,
  linesText,
  showsBottomTotals,
  showsGroupTotals,
  subtotalLabel as subtotalLabelOf,
  totalText,
  totalsRowText
} from '../utils/invoiceDetailReport';

// The columns, groupings and totals modes are shared with the server's export
// (utils/invoiceDetailReport.js), so the PDF prints each figure as shown here.

const DATE_PRESETS = [
  { value: 'this-month', label: 'This Month' },
  { value: 'last-month', label: 'Last Month' },
  { value: 'this-quarter', label: 'This Quarter' },
  { value: 'last-quarter', label: 'Last Quarter' },
  { value: 'year-to-date', label: 'Year to Date' },
  { value: 'last-year', label: 'Last Year' },
  { value: 'today', label: 'Today' },
  { value: 'this-week', label: 'This Week' },
  { value: 'custom', label: 'Custom' }
];

const DATE_FIELDS = [
  { value: 'invoice_date', label: 'Invoice Date' },
  { value: 'due_date', label: 'Due Date' }
];

/*
 * The report's own key names over the shared resolver. The arithmetic lives in
 * DateRangeFilter, where every search screen reads it from — two copies of
 * "the first of last quarter" is two chances to get it wrong.
 *
 * The report keeps its own DATE_PRESETS list rather than the shared one: it has
 * no All Time, deliberately. Unbounded, its facets are 91,304 invoice numbers
 * and the options query alone takes ~2.7s.
 */
function resolveDateRange(preset) {
  const { from, to } = sharedResolveDateRange(preset);
  return { startDate: from, endDate: to };
}

// The state the report opens in, and the state Reset returns it to. One
// factory so the two cannot drift — a Reset that leaves something set is worse
// than no Reset, because the user believes they are looking at everything.
//
// The period goes back to This Month rather than to nothing. Unbounded, the
// filter facets are 91,304 invoice numbers and 26,401 references, which is not
// a dropdown, and the options query takes ~2.7s against ~5s of scanning for
// the report itself. A period report's period is its spine.
function defaultFilters() {
  const { startDate, endDate } = resolveDateRange('this-month');
  return {
    preset: 'this-month',
    startDate,
    endDate,
    dateField: 'invoice_date',
    contactIds: [],
    includeCredit: false,
    includeVoided: false,
    totalsMode: 'per-group',
    visibleColumns: COLUMNS.filter(c => c.default).map(c => c.key),
    // Row-2 filters
    groupBy: 'contact',
    dates: [],
    productCodes: [],
    accountCodes: [],
    reps: [],
    statuses: [],
    references: [],
    invoiceNumbers: [],
    // Header-menu only — no dropdown in the filter row.
    sources: [],
    taxTypes: []
  };
}

// Which columns can be filtered by right-clicking their header, and the report
// filter each one drives.
//
// The column menu and the filter-row dropdown are two faces of the SAME state
// — there is no second, hidden filter behind the header. Tick a status here and
// the Status dropdown shows it selected, and vice versa. `facet` names the
// server facet the options come from; `source` and `tax_type` have no dropdown
// in the filter row at all and are reachable only this way, which is the point
// of the feature: the row cannot grow a picker for every question anyone has
// ever asked of the table.
const COLUMN_FILTERS = {
  invoice_no:   { label: 'Invoice #',     filterKey: 'invoiceNumbers', facet: 'invoiceNumbers' },
  contact:      { label: 'Contact',       filterKey: 'contactIds',     facet: 'contacts' },
  description:  { label: 'Product',       filterKey: 'productCodes',   facet: 'productCodes' },
  item_code:    { label: 'Item #',        filterKey: 'productCodes',   facet: 'productCodes' },
  account_code: { label: 'Sales Account', filterKey: 'accountCodes',   facet: 'accountCodes' },
  account_rep:  { label: 'Rep',           filterKey: 'reps',           facet: 'reps' },
  status:       { label: 'Status',        filterKey: 'statuses',       facet: 'statuses' },
  reference:    { label: 'Reference',     filterKey: 'references',     facet: 'references' },
  source:       { label: 'Source',        filterKey: 'sources',        facet: 'sources' },
  tax_type:     { label: 'Tax Type',      filterKey: 'taxTypes',       facet: 'taxTypes' }
};

// The date columns are conditional. There is ONE date filter and it applies to
// whichever column `dateField` selects, so offering it on the Due header while
// the report is keyed on invoice date would filter a column the user is not
// looking at — and silently, since both are dates.
const DATE_COLUMN_FILTER = { label: 'Date', filterKey: 'dates', facet: 'dates' };

// The filter fields that change the ROW SET, and so the cross-filtered options.
// Deliberately excludes the presentation-only ones — grouping, totals mode and
// the column set change how the same rows are drawn, not which rows they are,
// and refetching the facets when someone toggles a column would be pure noise.
const ROW_SET_FILTER_KEYS = [
  'startDate', 'endDate', 'dateField', 'includeCredit', 'includeVoided',
  'contactIds', 'productCodes', 'accountCodes', 'reps', 'statuses',
  'references', 'dates', 'invoiceNumbers', 'sources', 'taxTypes'
];

function InvoiceDetailReport({ user }) {
  // --- Filter state (persisted across nav) ---------------------------------
  const [filters, setFilters] = usePersistedState('invoiceDetailReport.filters', defaultFilters);

  const [contacts, setContacts] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [rows, setRows] = useState([]);
  // From the server with every page: each group on this page's WHOLE line
  // count and subtotal, whether it carries on from the page before
  // (continued) and whether it ends here; and the totals of every line on the
  // filters.
  const [groupMeta, setGroupMeta] = useState([]);
  const [totals, setTotals] = useState(null);
  const [groupCount, setGroupCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedContactId, setSelectedContactId] = usePersistedState('invoiceDetailReport.contactModalId', null);
  const [selectedInvoiceId, setSelectedInvoiceId] = usePersistedState('invoiceDetailReport.invoiceModalId', null);
  const [showExportModal, setShowExportModal] = useState(false);
  // `{}` means "not fetched yet", which is NOT the same as "no options". An
  // empty array is a real answer — nothing in the period matches — and blanks
  // the dropdown; undefined leaves it showing everything until the first
  // response lands, so the page doesn't open with eight empty pickers.
  const [filterOptions, setFilterOptions] = useState({});
  // True once a report has actually been run. Reset puts it back to false so
  // the table can say "cleared" rather than "nothing matched", which are very
  // different things to tell someone.
  const [hasRun, setHasRun] = useState(false);
  // Column sort (applied live, not gated by Update). Done on the server, over
  // every page: the lines within each group, AND the order of the groups.
  const [sort, setSort] = usePersistedState('invoiceDetailReport.sort', { field: '', direction: 'asc' });
  const hasFetchedRef = useRef(false);

  // Backfill any missing fields on persisted filter state — earlier versions
  // didn't have the multi-select array keys, and reading `.length` on an
  // undefined field would crash the first render.
  useEffect(() => {
    setFilters(prev => {
      const base = defaultFilters();
      const merged = { ...base, ...prev };
      // Anything the stored state carried as null/undefined falls back to the
      // default rather than reaching a `.length` on the first render. Written
      // as a loop over the defaults so a filter added later is covered without
      // anyone remembering to add a line here — the previous hand-listed
      // version had every key spelled twice and drifted.
      for (const key of Object.keys(base)) {
        if (merged[key] == null) merged[key] = base[key];
      }
      // Migrate the legacy single contactId to the multi-select array.
      if (!merged.contactIds?.length && prev.contactId) {
        merged.contactIds = [String(prev.contactId)];
      }
      return merged;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useSlideOutButton([rows.length, loading, hasRun]);


  // --- Load contacts + inventory items once (for the filter dropdowns) ----
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [contactsRes, itemsRes] = await Promise.all([
          fetch(`${API_URL}/api/contacts/search?contactStatus=ACTIVE&accountType=CUSTOMER&limit=10000`, { credentials: 'include' }),
          fetch(`${API_URL}/api/pricing/inventory-items`, { credentials: 'include' })
        ]);
        const contactsData = await contactsRes.json();
        const itemsData = await itemsRes.json();
        if (alive) {
          setContacts(contactsData.contacts || []);
          setInventoryItems(Array.isArray(itemsData) ? itemsData : (itemsData.items || itemsData.data || []));
        }
      } catch (e) {
        // Non-fatal — filters just won't be pre-populated.
        console.error('Failed to load filter sources:', e);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Build query params from a *snapshot* of filters — never from the live
  // edit state. Page/limit are passed separately so callers can change just
  // the page without re-applying filters. Same shape is reused by the
  // report endpoint, filter-options, and the Variation Summary modal so
  // they all see the same row set.
  // Every filter, with no paging. Shared verbatim by the report and the
  // filter-options endpoint so the dropdowns can never offer a value the
  // report would not return.
  const buildFilterParams = (snap) => {
    const p = new URLSearchParams({
      startDate: snap.startDate || '',
      endDate: snap.endDate || '',
      dateField: snap.dateField,
      includeCredit: snap.includeCredit ? '1' : '0',
      includeVoided: snap.includeVoided ? '1' : '0'
    });
    (snap.contactIds || []).forEach(v => p.append('contactIds', v));
    (snap.productCodes || []).forEach(v => p.append('productCodes', v));
    (snap.accountCodes || []).forEach(v => p.append('accountCodes', v));
    (snap.reps || []).forEach(v => p.append('reps', v));
    (snap.statuses || []).forEach(v => p.append('statuses', v));
    (snap.references || []).forEach(v => p.append('references', v));
    (snap.dates || []).forEach(v => p.append('dates', v));
    (snap.invoiceNumbers || []).forEach(v => p.append('invoiceNumbers', v));
    (snap.sources || []).forEach(v => p.append('sources', v));
    (snap.taxTypes || []).forEach(v => p.append('taxTypes', v));
    return p;
  };

  // The report's own request: every filter, the grouping and the sort (the
  // server orders by them, so a group cut by a page carries on on the next),
  // and the page. The export sends the same string, so it reads the same lines.
  const buildParams = (snap, page = 1, limit = 200, sortSnap = sort) => {
    const offset = Math.max(0, (page - 1) * limit);
    const p = buildFilterParams(snap);
    p.set('groupBy', snap.groupBy || 'contact');
    if (sortSnap?.field) {
      p.set('sortField', sortSnap.field);
      p.set('sortDir', sortSnap.direction === 'desc' ? 'desc' : 'asc');
    }
    p.set('limit', String(limit));
    p.set('offset', String(offset));
    return p;
  };

  // Filters that are CURRENTLY APPLIED to the report — the snapshot of
  // what was sent to the backend last. Filter edits to `filters` don't
  // take effect until the user clicks Update, which copies `filters`
  // into `appliedFilters` and triggers a fetch.
  const [appliedFilters, setAppliedFilters] = useState(filters);

  // Pagination — page + limit drive the server-side LIMIT/OFFSET. total &
  // totalPages come back from the server so PaginationControls can render.
  const [pagination, setPagination] = usePersistedState('invoiceDetailReport.pagination', {
    page: 1,
    limit: 200,
    total: 0,
    totalPages: 1
  });

  // --- Fetch report rows ---------------------------------------------------
  // Always uses an explicit snapshot rather than reading `filters`, so the
  // function is pure-by-args and safe to call from any callback.
  const fetchReport = async (snapshot, page = 1, limit = pagination.limit || 200, sortSnap = sort) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/reports/invoice-detail?${buildParams(snapshot, page, limit, sortSnap)}`, { credentials: 'include' });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setRows(data.rows || []);
      setGroupMeta(Array.isArray(data.groups) ? data.groups : []);
      setTotals(data.totals || null);
      setGroupCount(Number(data.groupCount) || 0);
      setAppliedFilters(snapshot);
      setPagination(prev => ({
        ...prev,
        page: data.page || page,
        limit: data.limit || limit,
        total: data.total || 0,
        totalPages: data.totalPages || 1
      }));
      setHasRun(true);
      // The dropdown options are NOT refreshed here. They follow the live
      // filter state via the debounced effect below, so they narrow as the
      // user picks rather than only after Update — and refreshing them here
      // too would just fire the same request twice.
    } catch (e) {
      setError(e.message);
      setRows([]);
      setGroupMeta([]);
      setTotals(null);
    } finally {
      setLoading(false);
    }
  };

  // Cross-filtered dropdown options. Every active filter is sent, and the
  // server narrows each dropdown by all the OTHERS — never by its own
  // selection, so a multi-select stays addable and a choice stays reversible.
  //
  // Driven off the LIVE filter state rather than the applied snapshot: the
  // point is that picking a status immediately narrows what the contact and
  // product dropdowns offer, before Update is ever pressed.
  const fetchFilterOptions = async (snapshot) => {
    try {
      const snap = snapshot || filters;
      const res = await fetch(`${API_URL}/api/reports/invoice-detail/filter-options?${buildFilterParams(snap)}`, { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      setFilterOptions(data || {});
    } catch (e) {
      // Non-fatal — the dropdowns keep the options they already had.
      console.error('Failed to load filter options:', e);
    }
  };

  // The user's hot-path update — snapshot current edits, reset to page 1,
  // fetch. Called from the Update button in the header.
  const handleUpdate = () => {
    fetchReport(filters, 1);
  };

  // Page change — keep the current applied snapshot, just shift the
  // window. No "dirty" considerations.
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchReport(appliedFilters, newPage, pagination.limit);
    }
  };

  // Run once on mount so the user sees the default (This Month) immediately.
  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchReport(filters, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-derive the cross-filtered dropdown options whenever a filter that
  // changes the ROW SET changes. Debounced because a multi-select fires per
  // tick and typing in a date field fires per keystroke — without it, picking
  // four statuses would be four round trips, and the last to return wins
  // rather than the last to be asked.
  const rowSetSignature = JSON.stringify(ROW_SET_FILTER_KEYS.map(k => filters[k]));
  useEffect(() => {
    const t = setTimeout(() => fetchFilterOptions(filters), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowSetSignature]);

  // "Dirty" — any edit-state filter differs from what's applied. Drives
  // the Update button's accent so the user knows there's a pending change.
  const isDirty = useMemo(() => {
    try {
      return JSON.stringify(filters) !== JSON.stringify(appliedFilters);
    } catch {
      return false;
    }
  }, [filters, appliedFilters]);

  // --- Filter handlers -----------------------------------------------------
  const handlePresetChange = (preset) => {
    setFilters(prev => ({
      ...prev,
      preset,
      ...(preset !== 'custom' ? resolveDateRange(preset) : {})
    }));
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value, ...(field === 'startDate' || field === 'endDate' ? { preset: 'custom' } : {}) }));
  };

  const handleColumnsChange = (next) => {
    setFilters(prev => ({ ...prev, visibleColumns: next }));
  };

  // Back to the state the report opens in, and drop the rows with it. Leaving
  // the previous result on screen under cleared filters is the worst of both:
  // it looks like an answer to a question nobody asked.
  const handleReset = () => {
    const fresh = defaultFilters();
    setFilters(fresh);
    setAppliedFilters(fresh);
    setSort({ field: '', direction: 'asc' });
    setRows([]);
    setGroupMeta([]);
    setTotals(null);
    setGroupCount(0);
    setError('');
    setHasRun(false);
    setPagination(prev => ({ ...prev, page: 1, total: 0, totalPages: 1 }));
  };

  /*
   * Anything differing from the state Reset returns to lights the Reset
   * button. The column-header filters live in the same `filters` object, so
   * this one comparison covers them too. The presentation-only keys (grouping,
   * totals mode, the column set) and the preset's name and date field are not
   * filters; start/end are passed under the names activeFilters treats as one
   * date filter, labelled Period so it cannot be mistaken for the Date column.
   */
  const headerFilters = (() => {
    const cleared = defaultFilters();
    const withDates = (f) => ({ ...f, dateFrom: f.startDate, dateTo: f.endDate });
    return activeHeaderFilters(withDates(filters), withDates(cleared), {
      ignore: ['preset', 'startDate', 'endDate', 'totalsMode', 'visibleColumns', 'groupBy'],
      labels: {
        date: 'Period', dates: 'Date', includeCredit: 'Credit notes', includeVoided: 'Voided',
        ...Object.fromEntries(Object.values(COLUMN_FILTERS).map(d => [d.filterKey, d.label])),
        // Product and Item # share one filter; name it for the product.
        productCodes: 'Product'
      }
    });
  })();
  const filtersApplied = headerFilters.count > 0;

  // --- Facet plumbing ------------------------------------------------------
  // Facets arrive as [{ value, count }] — the count is what the column-header
  // menus show. The dropdowns want bare values, so they are unwrapped once
  // here rather than in eight places. The scalar branch is not dead code: a
  // browser holding a cached bundle against a newer server, or the reverse,
  // should degrade to a working dropdown rather than a list of "[object
  // Object]".
  const facetValues = useMemo(() => {
    const out = {};
    for (const [key, list] of Object.entries(filterOptions)) {
      if (!Array.isArray(list)) continue;
      out[key] = list.map(o => (o && typeof o === 'object' ? o.value : o));
    }
    return out;
  }, [filterOptions]);

  // --- Cross-filtered dropdown sources -------------------------------------
  // Contact and Product are the two dropdowns fed from a full reference list
  // rather than from the report rows, so they are narrowed here against the
  // facet the server returned. `undefined` means the options have not arrived
  // yet — show everything rather than nothing.
  const allowedContactIds = useMemo(
    () => (facetValues.contacts ? new Set(facetValues.contacts.map(String)) : null),
    [facetValues.contacts]
  );
  const visibleContacts = useMemo(
    () => (allowedContactIds ? contacts.filter(c => allowedContactIds.has(String(c.id))) : contacts),
    [contacts, allowedContactIds]
  );

  const allowedProductCodes = useMemo(
    () => (facetValues.productCodes ? new Set(facetValues.productCodes.map(v => String(v).toLowerCase())) : null),
    [facetValues.productCodes]
  );
  const visibleProducts = useMemo(
    () => (allowedProductCodes
      ? inventoryItems.filter(p => allowedProductCodes.has(String(p.item_code || p.id).toLowerCase()))
      : inventoryItems),
    [inventoryItems, allowedProductCodes]
  );

  // Sales accounts come FROM the facet rather than from ACCOUNT_NAMES filtered
  // by it, so the dropdown offers the database's own casing.
  //
  // That matters because two controls now write this same filter — this
  // dropdown and the right-click menu on the Sales Account column — and the
  // menu's options are the facet's. ACCOUNT_NAMES is keyed lower-case ('200a1')
  // while the column is citext and stores '200A1'; filtering through one
  // control and then opening the other would show nothing selected, because
  // the strings differ even though Postgres treats them as equal. Sourcing both
  // from the facet makes them literally the same string. ACCOUNT_NAMES is a
  // case-insensitive Proxy, so the label resolves either way.
  const visibleAccountCodes = useMemo(() => {
    if (!facetValues.accountCodes) return Object.entries(ACCOUNT_NAMES);
    return facetValues.accountCodes.map(code => [String(code), ACCOUNT_NAMES[code] || '']);
  }, [facetValues.accountCodes]);

  // A facet the server had to cut short. Shown next to its dropdown, because a
  // list that looks complete and is not is how somebody concludes an invoice
  // does not exist.
  const truncatedNote = (key) => {
    const total = filterOptions.truncated?.[key];
    if (!total) return null;
    const shown = (facetValues[key] || []).length;
    return (
      <span className="filter-truncated" title={`${total.toLocaleString()} values match — narrow the period to see them all`}>
        {shown.toLocaleString()} of {total.toLocaleString()}
      </span>
    );
  };

  // Column-header sort — toggles asc → desc → asc for the clicked column, and
  // asks the server again: the sort runs over every page, so the page on screen
  // is the first of the newly sorted report.
  const handleSort = (field) => {
    const next = sort.field === field
      ? { field, direction: sort.direction === 'asc' ? 'desc' : 'asc' }
      : { field, direction: 'asc' };
    setSort(next);
    if (hasRun) fetchReport(appliedFilters, 1, pagination.limit, next);
  };

  const colByKey = useMemo(() => Object.fromEntries(COLUMNS.map(c => [c.key, c])), []);

  // --- Grouped row structure -----------------------------------------------
  // The server orders the lines by the applied grouping and sort and says
  // which group each is in; here they are only gathered up, in that order,
  // with each group's whole line count and subtotal from the server.
  const groups = useMemo(() => groupRows(rows, groupMeta), [rows, groupMeta]);

  const overallTotals = totals || {};

  // Subtotal caption for one group — utils/invoiceDetailReport, which the
  // server's PDF uses too, so a document cannot caption a group differently
  // from the screen it was produced from.
  const subtotalLabelFor = (group) => subtotalLabelOf(group, appliedFilters.groupBy);

  // The grand total is every line on the filters, on every page.
  const grandTotalCaption = grandTotalLabel(pagination.total || 0);

  // Use *applied* columns so toggling the Columns multi-select doesn't
  // change the table until Update is pressed.
  const visibleColSet = useMemo(() => new Set(appliedFilters.visibleColumns), [appliedFilters.visibleColumns]);
  const visibleColumns = COLUMNS.filter(c => visibleColSet.has(c.key));

  // --- Right-click a column header to filter it ----------------------------
  // Reuses ColumnFilterMenu, the same popover the search screens use, but
  // backed by this report's OWN filter state rather than a second set the hook
  // would keep. One source of truth: tick PAID here and the Status dropdown
  // shows it selected; clear it there and the tick goes.
  //
  // Like the dropdowns, this stages a change rather than applying it — the
  // Update button goes to "Update *" and the report refreshes when pressed.
  // Applying instantly would be a second, contradictory rule on a screen whose
  // every other control is staged.
  const [columnMenu, setColumnMenu] = useState({ key: null, anchor: null });

  // Only the columns actually on screen are filterable, and the date column
  // only when it is the one the period is keyed on.
  const columnFilterDefs = useMemo(() => {
    const defs = {};
    for (const col of visibleColumns) {
      if (COLUMN_FILTERS[col.key]) { defs[col.key] = COLUMN_FILTERS[col.key]; continue; }
      const isActiveDateCol = (col.key === 'invoice_date' && appliedFilters.dateField !== 'due_date')
        || (col.key === 'due_date' && appliedFilters.dateField === 'due_date');
      if (isActiveDateCol) defs[col.key] = DATE_COLUMN_FILTER;
    }
    return defs;
  }, [visibleColumns, appliedFilters.dateField]);

  // Human text for a facet value. The stored value is what the filter needs —
  // an account id, a product code — and is often not what anybody would
  // recognise, so the menu is given a label to show and search on.
  const contactsById = useMemo(
    () => new Map(contacts.map(c => [String(c.id), c])), [contacts]);
  const productsByCode = useMemo(
    () => new Map(inventoryItems.map(p => [String(p.item_code || p.id).toLowerCase(), p])), [inventoryItems]);

  const facetOptionsFor = (colKey) => {
    const def = columnFilterDefs[colKey];
    if (!def) return [];
    const raw = filterOptions[def.facet] || [];
    return raw.map((o) => {
      const value = String(o && typeof o === 'object' ? o.value : o);
      const count = (o && typeof o === 'object' ? o.count : undefined);
      let label = value;
      if (def.facet === 'contacts') {
        const c = contactsById.get(value);
        label = c ? [c.account_no, c.company_name].filter(Boolean).join(' · ') : `Account ${value}`;
      } else if (def.facet === 'productCodes') {
        const p = productsByCode.get(value.toLowerCase());
        label = p ? `${value} · ${p.item_name || p.description || ''}`.trim() : value;
      } else if (def.facet === 'accountCodes') {
        label = ACCOUNT_NAMES[value] ? `${value} · ${ACCOUNT_NAMES[value]}` : value;
      } else if (def.facet === 'dates') {
        label = formatDateCompact(value);
      } else if (def.facet === 'sources') {
        label = value.replace(/_/g, ' ');
      }
      return { value, label, count };
    });
  };

  // The adapter ColumnFilterMenu consumes. Its model is "untouched means every
  // value is ticked", which maps onto this report's "empty array means no
  // filter" — so a selection covering every option is stored as no filter at
  // all, rather than as a literal list of everything that would then have to be
  // maintained as the period changes underneath it.
  const columnFilters = {
    definitions: columnFilterDefs,
    menu: columnMenu,
    allRows: rows,
    hasFacets: true,
    // An empty selection here IS the absence of a filter, so a "Clear all"
    // button would do the opposite of what it says.
    allowClearAll: false,
    // Mapped onto the facet's own casing before the menu sees it. Both controls
    // write the same strings now, but a filter persisted by an older bundle —
    // or restored from sessionStorage — can still hold '200a1' against a facet
    // offering '200A1', and an unticked box beside a filter that IS applied is
    // the kind of thing someone debugs for twenty minutes.
    selected: useMemo(() => {
      const out = {};
      for (const [colKey, def] of Object.entries(columnFilterDefs)) {
        const chosen = filters[def.filterKey];
        if (!chosen?.length) continue;
        const byLower = new Map(facetOptionsFor(colKey).map(o => [o.value.toLowerCase(), o.value]));
        out[colKey] = chosen.map(v => byLower.get(String(v).toLowerCase()) ?? String(v));
      }
      return out;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, columnFilterDefs, filterOptions]),
    optionsFor: facetOptionsFor,
    isFiltered: (colKey) => !!(filters[columnFilterDefs[colKey]?.filterKey] || []).length,
    toggle: (colKey, value) => {
      const def = columnFilterDefs[colKey];
      if (!def) return;
      const all = facetOptionsFor(colKey).map(o => o.value);
      const byLower = new Map(all.map(v => [v.toLowerCase(), v]));
      // Canonicalised the same way `selected` is, so toggling off a value that
      // was stored in the other casing actually removes it.
      const current = filters[def.filterKey]?.length
        ? filters[def.filterKey].map(v => byLower.get(String(v).toLowerCase()) ?? String(v))
        : all;
      const next = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
      handleFilterChange(def.filterKey, next.length === all.length ? [] : next);
    },
    selectAll: (colKey) => {
      const def = columnFilterDefs[colKey];
      if (def) handleFilterChange(def.filterKey, []);
    },
    clearAll: (colKey) => {
      const def = columnFilterDefs[colKey];
      if (def) handleFilterChange(def.filterKey, []);
    },
    openMenu: (key, anchor) => setColumnMenu({ key, anchor }),
    closeMenu: () => setColumnMenu({ key: null, anchor: null })
  };

  // Spread onto a <th>. stopPropagation keeps a right-click on the header off
  // any table-level context menu, and preventDefault keeps the browser's own
  // menu out of the way.
  const columnHeaderProps = (colKey) => (columnFilterDefs[colKey] ? {
    onContextMenu: (e) => {
      e.preventDefault();
      e.stopPropagation();
      setColumnMenu({ key: colKey, anchor: e.currentTarget });
    }
  } : {});


  // Render a single line row (the leaf cells of the table).
  const renderCell = (col, row) => {
    let val = row[col.key];
    if (col.key === 'invoice_date' || col.key === 'due_date') {
      return val ? formatDateCompact(val) : '';
    }
    if (col.key === 'invoice_no') {
      return (
        <span
          className="invoice-link"
          onClick={(e) => { e.stopPropagation(); setSelectedInvoiceId(row.invoice_id); }}
        >
          {val || ''}
        </span>
      );
    }
    if (col.key === 'contact') {
      return (
        <span
          className="contact-link"
          onClick={(e) => { e.stopPropagation(); setSelectedContactId(row.account_id); }}
        >
          {row.account_no && <span className="account-badge-inline">{row.account_no}</span>}
          {row.account_name || ''}
        </span>
      );
    }
    if (col.key === 'status') {
      return <span className={`status-pill status-${(val || '').toLowerCase()}`}>{val || ''}</span>;
    }
    if (col.key === 'source') {
      const text = (val || '').replace(/_/g, ' ');
      const isXero = (val || '').toLowerCase() === 'xero';
      return <span className={`source-pill ${isXero ? 'source-xero' : ''}`}>{text}</span>;
    }
    if (col.key === 'description') {
      // Embed the item code as a small chip below the description (only
      // shown when the Item Code column isn't independently visible —
      // otherwise the user has explicitly chosen to see it in its own col).
      const showInlineCode = row.item_code && !visibleColSet.has('item_code');
      return (
        <div className="desc-cell-inner">
          <span className="desc-text" title={val || ''}>{val || ''}</span>
          {showInlineCode && <span className="product-code">{row.item_code}</span>}
        </div>
      );
    }
    if (col.zeroAsDash && (val == null || parseFloat(val) === 0)) {
      return <span className="muted">—</span>;
    }
    if (col.isPrice) {
      return val != null && val !== '' ? formatPrice(val) : '';
    }
    if (col.isCurrency) {
      return val != null && val !== '' ? formatCurrency(val) : '';
    }
    if (col.isNumber) {
      return formatQuantity(val);
    }
    return val == null ? '' : String(val);
  };

  // --- Render --------------------------------------------------------------
  // Totals visibility follows the applied snapshot, not the live edit.
  const showGroupTotals = showsGroupTotals(appliedFilters.totalsMode);
  const showBottomTotals = showsBottomTotals(appliedFilters.totalsMode);

  // --- PDF export ----------------------------------------------------------
  // Built on the SERVER (POST /api/reports/invoice-detail/export), from every
  // line on the applied filters — or the page on screen, if chosen in the
  // export window — grouped, sorted and totalled as the screen shows it
  // (utils/invoiceDetailReport.exportTable, the same helpers the table uses).
  // A month is several thousand lines, far past what a screen may post, and
  // the totals must be every line's, not a page's.
  //
  // What goes from here is the request: the applied snapshot's query string
  // (never the live, un-applied edits), the columns on screen, the totals
  // mode, and the criteria printed above the table.

  // What produced this page, printed above the data. A table of figures with no
  // record of the filters behind it is a page nobody can check later.
  const buildExportCriteria = () => {
    const snap = appliedFilters;
    const out = [];
    const labelOf = (options, value) => options.find(o => o.value === value)?.label || value;
    // Up to three are named outright; past that the count is more use than a
    // line of truncated names.
    const summarise = (values, render) => {
      const arr = values || [];
      if (!arr.length) return null;
      return arr.length <= 3 ? arr.map(render).join(', ') : `${arr.length} selected`;
    };

    out.push({
      label: labelOf(DATE_FIELDS, snap.dateField),
      value: `${formatDateShort(snap.startDate)} – ${formatDateShort(snap.endDate)}`
    });
    out.push({ label: 'Grouped by', value: labelOf(GROUPING_OPTIONS, snap.groupBy || 'contact') });
    out.push({ label: 'Totals', value: labelOf(TOTALS_MODES, snap.totalsMode) });
    if (sort.field) {
      out.push({
        label: 'Sorted by',
        value: `${colByKey[sort.field]?.label || sort.field} ${sort.direction === 'asc' ? '↑' : '↓'}`
      });
    }

    const include = [snap.includeCredit && 'credit notes', snap.includeVoided && 'voided'].filter(Boolean);
    out.push({ label: 'Including', value: include.length ? include.join(' + ') : 'invoices only' });

    const contactNames = summarise(snap.contactIds,
      (id) => contacts.find(c => String(c.id) === String(id))?.company_name || id);
    if (contactNames) out.push({ label: 'Contact', value: contactNames });

    const products = summarise(snap.productCodes, (code) => code);
    if (products) out.push({ label: 'Product', value: products });

    const accounts = summarise(snap.accountCodes,
      (code) => (ACCOUNT_NAMES[code] ? `${code} ${ACCOUNT_NAMES[code]}` : code));
    if (accounts) out.push({ label: 'Sales account', value: accounts });

    const reps = summarise(snap.reps, (r) => r);
    if (reps) out.push({ label: 'Rep', value: reps });

    const statuses = summarise(snap.statuses, (st) => st);
    if (statuses) out.push({ label: 'Status', value: statuses });

    const invoiceNumbers = summarise(snap.invoiceNumbers, (n) => n);
    if (invoiceNumbers) out.push({ label: 'Invoice #', value: invoiceNumbers });

    const references = summarise(snap.references, (r) => r);
    if (references) out.push({ label: 'Reference', value: references });

    const dates = summarise(snap.dates, (d) => formatDateCompact(d));
    if (dates) out.push({ label: 'Date', value: dates });

    return out;
  };

  // The export window's own picture of the export (its column and total
  // choices, the row count, the "All results / This page" choice) plus the
  // request it is sent as. `scope` comes from the window: 'all' (the default)
  // is every line on the filters, 'page' the page on screen.
  const buildExportPayload = ({ scope = 'all' } = {}) => {
    const snap = appliedFilters;
    const wholeReport = scope !== 'page';
    const lineCount = wholeReport ? (pagination.total || 0) : rows.length;
    const totalColumns = visibleColumns.filter(isTotalColumn);
    const anyTotals = showGroupTotals || showBottomTotals;
    return {
      title: 'Invoice Detail',
      kicker: 'REPORT',
      filename: `invoice-detail-${snap.startDate}-to-${snap.endDate}`
        + (!wholeReport && pagination.totalPages > 1 ? `-page-${pagination.page}` : ''),
      columns: visibleColumns.map(c => ({ key: c.key, label: c.label, numeric: c.align === 'right' })),
      rows: [],
      groups: [],
      // The grand total as shown — the window offers a chip per figure; the
      // server prints the figures itself.
      totals: showBottomTotals && totals ? [totalsRowText(totals, visibleColumns)] : [],
      totalKeys: anyTotals ? totalColumns.map(c => c.key) : [],
      criteria: buildExportCriteria(),
      rowNoun: 'lines',
      rowCount: lineCount,
      bodyRowCount: lineCount,
      groupCount: wholeReport ? groupCount : groups.length,
      available: { all: pagination.total || 0, page: rows.length },
      request: {
        query: buildParams(snap, pagination.page || 1, pagination.limit || 200, sort).toString(),
        scope: wholeReport ? 'all' : 'page',
        totalsMode: snap.totalsMode
      }
    };
  };

  // What the export window posts: the request, the columns still chosen and
  // the totals still wanted (a column left out takes its total with it).
  const exportRequestFor = (shaped, { hiddenTotals } = {}) => {
    const columns = (shaped.columns || []).map(c => c.key);
    const chosen = new Set(columns);
    const hidden = hiddenTotals || new Set();
    return {
      ...shaped.request,
      columns,
      totalKeys: (shaped.totalKeys || []).filter(k => chosen.has(k) && !hidden.has(k)),
      criteria: shaped.criteria || []
    };
  };

  return (
    <div className="invoice-detail-report">
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1' }}>
          <span className="info">Invoice Detail report</span>
          <span className="note">
            <span className="material-symbols-rounded">info</span>
            <span style={{ margin: 'auto 0' }}>One row per invoice line. Grouped by contact (parent + children together).</span>
          </span>
        </div>
        <div className="search-header-inner" style={{ gridColumn: '2' }}>
          <div className="buttons slideout">
            <div>
              <button
                className={clearFiltersClass(filtersApplied)}
                onClick={handleReset}
                disabled={loading}
                title={filtersApplied ? 'Clear every filter and the results, back to how the report opens' : 'No filters applied'}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">filter_alt_off</span>
                </div>
                <span className="btn-label">Reset</span>
              </button>
            </div>
            <div>
              <button
                className="btn-slideout"
                onClick={() => setShowExportModal(true)}
                disabled={loading || rows.length === 0}
                title={rows.length === 0
                  ? 'Run the report first'
                  : 'Save, print or email this report as a PDF'}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">ios_share</span>
                </div>
                <span className="btn-label">Export PDF</span>
              </button>
            </div>
            <div>
              <button
                className={`btn-slideout report-update-btn ${isDirty ? 'is-dirty' : ''}`}
                onClick={handleUpdate}
                disabled={loading}
                title={isDirty ? 'Apply pending filter changes' : 'Refresh report with current filters'}
              >
                <div className="icon-wrapper">
                  <span className={`material-symbols-rounded ${loading ? 'spinning' : ''}`}>
                    {loading ? 'sync' : 'refresh'}
                  </span>
                </div>
                <span className="btn-label">{isDirty ? 'Update *' : 'Update'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="search-filters">
        <div className="filter-row">
          {/* Connected From / To / Date-Range trio. The grid cells are tight
              (we shrink the gap with negative margins in CSS) and the
              border-radii are adjusted so they read as one combined input
              with a final dropdown arrow on the right. */}
          <div className="filter-group date-from-cell">
            <DateInput
              label="From"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
            />
          </div>
          <div className="filter-group date-to-cell">
            <DateInput
              label="To"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
            />
          </div>
          <div className="filter-group date-preset-cell" title="Date range presets">
            <label>&nbsp;</label>
            <Dropdown
              value={filters.preset}
              onChange={handlePresetChange}
              options={DATE_PRESETS}
            />
          </div>
          <div className="filter-group">
            <label>Date Field</label>
            <Dropdown
              value={filters.dateField}
              onChange={(v) => handleFilterChange('dateField', v)}
              options={DATE_FIELDS}
            />
          </div>
          <div className="filter-group">
            <label>Grouping</label>
            <Dropdown
              value={filters.groupBy}
              onChange={(v) => handleFilterChange('groupBy', v)}
              options={GROUPING_OPTIONS}
            />
          </div>
          <div className="filter-group">
            <label>Totals</label>
            <Dropdown
              value={filters.totalsMode}
              onChange={(v) => handleFilterChange('totalsMode', v)}
              options={TOTALS_MODES}
            />
          </div>
          <div className="filter-group">
            <label>Columns</label>
            <MultiSelectDropdown
              value={filters.visibleColumns || []}
              onChange={handleColumnsChange}
              options={COLUMNS.map(c => ({ value: c.key, label: c.label }))}
              placeholder={`${(filters.visibleColumns || []).length} selected`}
            />
          </div>
          <div className="filter-group">
            <label>Status</label>
            <MultiSelectDropdown
              value={filters.statuses || []}
              onChange={(v) => handleFilterChange('statuses', v)}
              selectedInMenu={true}
              options={(facetValues.statuses || []).map(s => ({
                value: s,
                // Render as a chip (matches the in-table status pill).
                label: <span className={`status-pill status-${(s || '').toLowerCase()}`}>{s}</span>,
                searchText: s
              }))}
              placeholder={(filters.statuses || []).length ? `${(filters.statuses || []).length} selected` : 'Any status'}
            />
          </div>
          <div className="filter-group">
            <label>CREDITS</label>
            <div className="checkbox-container">
              <div className="check light">
                <input
                  id="report-include-credit"
                  type="checkbox"
                  checked={!!filters.includeCredit}
                  onChange={(e) => handleFilterChange('includeCredit', e.target.checked)}
                />
                <label htmlFor="report-include-credit"></label>
              </div>
            </div>
          </div>
          <div className="filter-group">
            <label>VOIDED</label>
            <div className="checkbox-container">
              <div className="check light">
                <input
                  id="report-include-voided"
                  type="checkbox"
                  checked={!!filters.includeVoided}
                  onChange={(e) => handleFilterChange('includeVoided', e.target.checked)}
                />
                <label htmlFor="report-include-voided"></label>
              </div>
            </div>
          </div>
        </div>
        {/* Row 2 — Contact + per-line multi-select filters. Each shows its
            selected items inside the open menu (selectedInMenu) rather than as
            tag chips below the trigger. */}
        <div className="filter-row filter-row-2">
          <div className="filter-group">
            <label>Contact{truncatedNote('contacts')}</label>
            <MultiSelectDropdown
              value={filters.contactIds || []}
              onChange={(v) => handleFilterChange('contactIds', v)}
              searchable={true}
              selectedInMenu={true}
              searchPlaceholder="Search by # or name…"
              options={visibleContacts.map(c => ({
                value: c.id.toString(),
                label: (
                  <div className="filter-option-coded">
                    {c.account_no && <span className="account-badge-inline">{c.account_no}</span>}
                    <span className="option-title">{c.company_name}</span>
                  </div>
                ),
                searchText: `${c.account_no || ''} ${c.company_name}`
              }))}
              placeholder={(filters.contactIds || []).length ? `${(filters.contactIds || []).length} selected` : 'Any contact'}
            />
          </div>
          <div className="filter-group">
            <label>Product{truncatedNote('productCodes')}</label>
            <MultiSelectDropdown
              value={filters.productCodes || []}
              onChange={(v) => handleFilterChange('productCodes', v)}
              searchable={true}
              selectedInMenu={true}
              searchPlaceholder="Search by code or description…"
              options={(visibleProducts || []).map(p => ({
                value: p.item_code || String(p.id),
                // Label rendered as JSX so the code shows as a chip under the
                // description (mirrors the Blends table style).
                label: (
                  <div className="filter-option-coded">
                    {p.item_code && <span className="product-code">{p.item_code}</span>}
                    <span className="option-title">{p.item_name || p.description || '(no name)'}</span>
                  </div>
                ),
                searchText: `${p.item_code || ''} ${p.item_name || ''} ${p.description || ''}`
              }))}
              placeholder={(filters.productCodes || []).length ? `${(filters.productCodes || []).length} selected` : 'Any product'}
            />
          </div>
          <div className="filter-group">
            <label>Sales Account</label>
            <MultiSelectDropdown
              value={filters.accountCodes || []}
              onChange={(v) => handleFilterChange('accountCodes', v)}
              searchable={true}
              selectedInMenu={true}
              searchPlaceholder="Search by code or name…"
              options={visibleAccountCodes.map(([code, name]) => ({
                value: code,
                label: (
                  <div className="filter-option-coded">
                    <span className="product-code">{code}</span>
                    <span className="option-title">{name}</span>
                  </div>
                ),
                searchText: `${code} ${name}`
              }))}
              placeholder={(filters.accountCodes || []).length ? `${(filters.accountCodes || []).length} selected` : 'Any account'}
            />
          </div>
          <div className="filter-group">
            <label>Rep</label>
            <MultiSelectDropdown
              value={filters.reps || []}
              onChange={(v) => handleFilterChange('reps', v)}
              selectedInMenu={true}
              options={(facetValues.reps || []).map(r => ({ value: r, label: r }))}
              placeholder={(filters.reps || []).length ? `${(filters.reps || []).length} selected` : 'Any rep'}
            />
          </div>
          <div className="filter-group">
            <label>Invoice #{truncatedNote('invoiceNumbers')}</label>
            <MultiSelectDropdown
              value={filters.invoiceNumbers || []}
              onChange={(v) => handleFilterChange('invoiceNumbers', v)}
              searchable={true}
              selectedInMenu={true}
              searchPlaceholder="Search invoice #…"
              options={(facetValues.invoiceNumbers || []).map(n => ({ value: n, label: n, searchText: n }))}
              placeholder={(filters.invoiceNumbers || []).length ? `${(filters.invoiceNumbers || []).length} selected` : 'Any invoice #'}
            />
          </div>
          <div className="filter-group">
            <label>Reference{truncatedNote('references')}</label>
            <MultiSelectDropdown
              value={filters.references || []}
              onChange={(v) => handleFilterChange('references', v)}
              searchable={true}
              selectedInMenu={true}
              searchPlaceholder="Search references…"
              options={(facetValues.references || []).map(r => ({ value: r, label: r, searchText: r }))}
              placeholder={(filters.references || []).length ? `${(filters.references || []).length} selected` : 'Any reference'}
            />
          </div>
          <div className="filter-group">
            <label>Date{truncatedNote('dates')}</label>
            <MultiSelectDropdown
              value={filters.dates || []}
              onChange={(v) => handleFilterChange('dates', v)}
              selectedInMenu={true}
              options={(facetValues.dates || []).map(d => ({ value: d, label: formatDateCompact(d) }))}
              placeholder={(filters.dates || []).length ? `${(filters.dates || []).length} selected` : 'Any date'}
            />
          </div>
        </div>
      </div>

      {/* Pagination — only when there's more than one page — and, whenever
          there are lines, a word that the totals are every page's. */}
      {(pagination.totalPages > 1 || (rows.length > 0 && !loading && !error)) && (
        <div className="report-pagination">
          {rows.length > 0 && !loading && !error && (
            <span
              className="totals-scope"
              title="Each group's subtotal and the bottom total count every line on these filters, on every page"
            >
              <span className="material-symbols-rounded">functions</span>
              Totals are for all {linesText(pagination.total || 0)}
            </span>
          )}
          {pagination.totalPages > 1 && (
            <PaginationControls
              pagination={pagination}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      )}

      <div className="table-container">
        {loading ? (
          <LoadingSpinner />
        ) : error ? (
          <div className="report-error">Failed to load report: {error}</div>
        ) : rows.length === 0 ? (
          <div className="report-empty">
            {hasRun
              ? 'No invoice lines for the selected filters.'
              : 'Filters cleared. Choose what you need and press Update.'}
          </div>
        ) : (
          <table className="data-table invoice-detail-table">
            <thead>
              <tr>
                {visibleColumns.map(col => {
                  const filterable = !!columnFilterDefs[col.key];
                  const filtered = filterable && columnFilters.isFiltered(col.key);
                  return (
                    <th
                      key={col.key}
                      data-col={col.key}
                      className={`sortable${col.align === 'right' ? 'text-right' : ''} ${sort.field === col.key ? 'is-sorted' : ''} ${filtered ? 'col-filtered' : ''}`}
                      onClick={() => handleSort(col.key)}
                      title={filterable
                        ? 'Click to sort · right-click to filter this column'
                        : 'Click to sort'}
                      {...columnHeaderProps(col.key)}
                    >
                      {/* No filter icon here — ColumnFilterMenu.css already
                          draws one via th.col-filtered::after, and the point of
                          the shared class is that a filtered header looks the
                          same on every screen. */}
                      <span className="th-label">{col.label}</span>
                      <span className="material-symbols-rounded sort-arrow">
                        {sort.field === col.key ? (sort.direction === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more'}
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {/* Where the totals start — the first column flagged as a
                  totals contributor (or quantity). Used to colSpan the
                  label cell and skip everything before the numeric tail. */}
              {(() => null)()}
              {(() => null)()}
              {groups.map(group => {
                // The WHOLE group's subtotal, from the server — every line of
                // it on these filters, whichever page they are on.
                const groupTotals = group.totals || {};
                const appliedGroupBy = appliedFilters.groupBy || 'contact';
                const isContactGrouping = appliedGroupBy === 'contact';
                const isChild = isContactGrouping && !!group.parentAccountId;
                const totalsStart = visibleColumns.findIndex(isTotalColumn);
                const labelSpan = totalsStart >= 0 ? totalsStart : visibleColumns.length;
                const tailCols = totalsStart >= 0 ? visibleColumns.slice(totalsStart) : [];
                // Subtotal label text varies by grouping dimension — see
                // subtotalLabelFor, shared with the PDF export.
                const subtotalLabel = subtotalLabelFor(group);
                return (
                  <React.Fragment key={group.key}>
                    {/* Group header row — content varies by grouping. */}
                    <tr className={`group-header ${isChild ? 'is-child' : 'is-parent'}`}>
                      <td colSpan={visibleColumns.length}>
                        <div className="group-header-inner">
                          {appliedGroupBy === 'contact' && (
                            <>
                              {isChild && (
                                <>
                                  <span className="material-symbols-rounded child-arrow" title="Child of parent contact">
                                    subdirectory_arrow_right
                                  </span>
                                  <span className="parent-link" title="Parent contact">
                                    {group.parentAccountName}
                                    {group.parentAccountNo && <span className="account-badge-inline">{group.parentAccountNo}</span>}
                                  </span>
                                  <span className="parent-separator">›</span>
                                </>
                              )}
                              <span
                                className="contact-link"
                                onClick={(e) => { e.stopPropagation(); setSelectedContactId(group.accountId); }}
                              >
                                {group.accountName}
                                {group.accountNo && <span className="account-badge-inline">{group.accountNo}</span>}
                              </span>
                            </>
                          )}
                          {appliedGroupBy === 'invoice_no' && (
                            <>
                              <span
                                className="invoice-link"
                                onClick={(e) => { e.stopPropagation(); setSelectedInvoiceId(group.invoiceId); }}
                              >
                                {group.invoiceNo}
                              </span>
                              <span className="muted">·</span>
                              <span>{formatDateCompact(group.invoiceDate)}</span>
                              <span className="muted">·</span>
                              <span
                                className="contact-link"
                                onClick={(e) => { e.stopPropagation(); setSelectedContactId(group.accountId); }}
                              >
                                {group.accountName}
                              </span>
                            </>
                          )}
                          {appliedGroupBy === 'item_code' && (
                            <>
                              {group.itemCode && <span className="product-code">{group.itemCode}</span>}
                              <span>{group.description || (group.itemCode ? '' : '(no code)')}</span>
                            </>
                          )}
                          {appliedGroupBy === 'account_code' && (
                            <>
                              <span className="product-code">{group.accountCode || '—'}</span>
                            </>
                          )}
                          {appliedGroupBy === 'status' && (
                            <span className={`status-pill status-${(group.status || '').toLowerCase()}`}>{group.status || '—'}</span>
                          )}
                          {group.continued && (
                            <span className="group-continued" title="This group started on an earlier page">(continued)</span>
                          )}
                          <span
                            className="group-row-count"
                            title={group.lineCount !== group.rows.length
                              ? `${linesText(group.lineCount)} in all — ${group.rows.length} on this page`
                              : undefined}
                          >
                            {linesText(group.lineCount)}
                          </span>
                        </div>
                      </td>
                    </tr>
                    {/* Line rows */}
                    {group.rows.map(row => (
                      <tr key={row.line_id} className="line-row">
                        {visibleColumns.map(col => (
                          <td
                            key={col.key}
                            data-col={col.key}
                            className={col.align === 'right' ? 'text-right' : ''}
                          >
                            {renderCell(col, row)}
                          </td>
                        ))}
                      </tr>
                    ))}
                    {/* Per-group subtotal — the whole group's, shown once,
                        on the page holding the group's last line. The label
                        cell colspans every column before the first totals
                        column, then one td per remaining column. */}
                    {showGroupTotals && group.ends && (
                      <tr className="group-subtotal">
                        <td colSpan={labelSpan}>
                          <strong>{subtotalLabel}</strong>
                        </td>
                        {tailCols.map(col => {
                          if (isTotalColumn(col)) {
                            const text = totalText(col, groupTotals[col.key]);
                            return (
                              <td key={col.key} className="text-right">
                                <strong className={text === '—' ? 'muted' : undefined}>{text}</strong>
                              </td>
                            );
                          }
                          return <td key={col.key} />;
                        })}
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {/* Overall totals at the bottom — same colSpan trick as the
                  per-group subtotals. */}
              {showBottomTotals && (() => {
                const totalsStart = visibleColumns.findIndex(isTotalColumn);
                const labelSpan = totalsStart >= 0 ? totalsStart : visibleColumns.length;
                const tailCols = totalsStart >= 0 ? visibleColumns.slice(totalsStart) : [];
                return (
                  <tr className="grand-total">
                    <td
                      colSpan={labelSpan}
                      title={pagination.totalPages > 1
                        ? `Every line on these filters — ${rows.length} of them on this page`
                        : undefined}
                    >
                      <strong>{grandTotalCaption}</strong>
                    </td>
                    {tailCols.map(col => {
                      if (isTotalColumn(col)) {
                        const text = totalText(col, overallTotals[col.key]);
                        return (
                          <td key={col.key} className="text-right">
                            <strong className={text === '—' ? 'muted' : undefined}>{text}</strong>
                          </td>
                        );
                      }
                      return <td key={col.key} />;
                    })}
                  </tr>
                );
              })()}
            </tbody>
          </table>
        )}
      </div>

      {selectedContactId && ReactDOM.createPortal(
        <ContactModal
          contactId={selectedContactId}
          onClose={() => setSelectedContactId(null)}
        />,
        document.body
      )}

      {selectedInvoiceId && (
        <InvoiceModal
          invoiceId={selectedInvoiceId}
          onClose={() => setSelectedInvoiceId(null)}
        />
      )}

      <ColumnFilterMenu cols={columnFilters} />

      {showExportModal && (
        <TableExportModal
          user={user}
          build={buildExportPayload}
          // Built on the server from every line (see buildExportPayload): the
          // window posts the request, not the rows.
          endpoint="/api/reports/invoice-detail/export"
          previewEndpoint="/api/reports/invoice-detail/export/preview"
          requestFor={exportRequestFor}
          title="Invoice Detail"
          modalTitle="Export the Invoice Detail report"
          // Twelve columns by default, so landscape unless the user has cut it
          // back to something that genuinely fits portrait.
          // Portrait and fitted to the page width, like every export — a wide
          // report shrinks to fit rather than turning the page.
          defaultOrientation="portrait"
          onClose={() => setShowExportModal(false)}
          onDone={(msg) => {
            setShowExportModal(false);
            if (msg) showAlert(msg, 'success');
          }}
        />
      )}

    </div>
  );
}

export default InvoiceDetailReport;
